import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, X } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import Label from "../../../../shared/ui/form/Label";
import Input from "../../../../shared/ui/form/input/InputField";
import { Modal } from "../../../../shared/ui/modal";
import {
    CUSTOM_FIELD_LABEL_MAX,
    CUSTOM_FIELD_TYPES,
    customFieldRefusal,
    useCreatePatientField,
    useUpdatePatientField,
    type CustomFieldType,
    type PatientCustomField,
} from "../../../center";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    centerId: string;
    /** The field being edited; a new one when absent. */
    field?: PatientCustomField | null;
}

const SELECT_CLASS = "field-base field-default h-11 w-full px-4 text-sm disabled:cursor-not-allowed disabled:opacity-60";

/** One yes-or-no of the field, as a switch with what it is for under it. */
function FieldSwitch({
    label,
    hint,
    checked,
    onChange,
}: {
    label: string;
    hint: string;
    checked: boolean;
    onChange: (value: boolean) => void;
}) {
    return (
        <label className="flex cursor-pointer items-start justify-between gap-4">
            <span className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-content-primary">{label}</span>
                <span className="text-xs text-content-tertiary">{hint}</span>
            </span>
            <input
                type="checkbox"
                role="switch"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                className="relative mt-1 h-5 w-9 shrink-0 cursor-pointer appearance-none rounded-full bg-gray-200 transition-colors before:absolute before:left-0.5 before:top-0.5 before:h-4 before:w-4 before:rounded-full before:bg-white before:transition-transform checked:bg-brand-500 checked:before:translate-x-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:bg-gray-700"
            />
        </label>
    );
}

/**
 * Defining one of the centre's own patient fields: what it is called, what
 * kind of value it holds — fixed once created, since the values already
 * given were written for it — and whether it must be filled or be unique.
 */
export default function PatientFieldFormModal({ isOpen, onClose, centerId, field }: Props) {
    const { t } = useTranslation();
    const create = useCreatePatientField(centerId);
    const update = useUpdatePatientField(centerId);
    const editing = Boolean(field);

    const [label, setLabel] = useState("");
    const [fieldType, setFieldType] = useState<CustomFieldType>("text");
    const [options, setOptions] = useState<string[]>([]);
    const [newOption, setNewOption] = useState("");
    const [required, setRequired] = useState(false);
    const [unique, setUnique] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        setLabel(field?.label ?? "");
        setFieldType(field?.field_type ?? "text");
        setOptions(field?.options ?? []);
        setNewOption("");
        setRequired(field?.required ?? false);
        setUnique(field?.unique ?? false);
        setSubmitted(false);
        setError(null);
    }, [isOpen, field]);

    const isSelect = fieldType === "select";
    const labelMissing = !label.trim();
    const optionsMissing = isSelect && options.length === 0;

    const addOption = () => {
        const option = newOption.trim();
        if (!option) return;
        if (!options.some((existing) => existing.toLocaleLowerCase() === option.toLocaleLowerCase())) {
            setOptions((current) => [...current, option]);
        }
        setNewOption("");
    };

    const handleSubmit = async () => {
        setSubmitted(true);
        setError(null);
        if (labelMissing || optionsMissing) return;
        const shared = {
            label: label.trim(),
            required,
            unique,
            ...(isSelect ? { options } : {}),
        };
        try {
            if (field) {
                await update.mutateAsync({ fieldId: field.id, changes: shared });
            } else {
                await create.mutateAsync({ ...shared, field_type: fieldType });
            }
            onClose();
        } catch (err) {
            setError(customFieldRefusal(err).message);
        }
    };

    const pending = create.isPending || update.isPending;
    const title = editing ? t("center.patientFields.editTitle") : t("center.patientFields.newTitle");

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            ariaLabel={title}
            className="mx-4 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-surface-raised p-6"
        >
            <form
                noValidate
                className="flex flex-col gap-5"
                onSubmit={(e) => {
                    e.preventDefault();
                    void handleSubmit();
                }}
            >
                <div className="pr-10">
                    <h3 className="text-lg font-semibold text-content-primary">{title}</h3>
                    <p className="mt-1 text-sm text-content-secondary">{t("center.patientFields.formSubtitle")}</p>
                </div>

                <div>
                    <Label htmlFor="patient-field-label">{t("center.patientFields.label")}</Label>
                    <Input
                        id="patient-field-label"
                        value={label}
                        onChange={(e) => setLabel(e.target.value.slice(0, CUSTOM_FIELD_LABEL_MAX))}
                        placeholder={t("center.patientFields.labelPlaceholder")}
                        error={submitted && labelMissing}
                        aria-invalid={submitted && labelMissing}
                        aria-describedby={submitted && labelMissing ? "patient-field-label-error" : undefined}
                    />
                    {submitted && labelMissing && (
                        <p id="patient-field-label-error" className="mt-1 text-xs text-status-critical">
                            {t("center.patientFields.errors.labelRequired")}
                        </p>
                    )}
                </div>

                <div>
                    <Label htmlFor="patient-field-type">{t("center.patientFields.type")}</Label>
                    <select
                        id="patient-field-type"
                        value={fieldType}
                        onChange={(e) => setFieldType(e.target.value as CustomFieldType)}
                        disabled={editing}
                        aria-describedby={editing ? "patient-field-type-hint" : undefined}
                        className={SELECT_CLASS}
                    >
                        {CUSTOM_FIELD_TYPES.map((option) => (
                            <option key={option} value={option}>
                                {t(`center.patientFields.types.${option}`)}
                            </option>
                        ))}
                    </select>
                    {editing && (
                        <p id="patient-field-type-hint" className="mt-1 text-xs text-content-tertiary">
                            {t("center.patientFields.typeLocked")}
                        </p>
                    )}
                </div>

                {isSelect && (
                    <fieldset className="flex flex-col gap-2">
                        <legend className="mb-1.5 text-sm font-medium text-gray-700 dark:text-gray-400">
                            {t("center.patientFields.options")}
                        </legend>
                        {options.length > 0 && (
                            <ul className="flex flex-wrap gap-2" aria-label={t("center.patientFields.options")}>
                                {options.map((option) => (
                                    <li
                                        key={option}
                                        className="inline-flex items-center gap-1 rounded-full bg-accent-tint py-1 pl-3 pr-1 text-sm text-accent-on-tint"
                                    >
                                        {option}
                                        <button
                                            type="button"
                                            onClick={() => setOptions((current) => current.filter((o) => o !== option))}
                                            aria-label={t("center.patientFields.removeOption", { option })}
                                            className="inline-flex size-6 items-center justify-center rounded-full hover:bg-accent/15"
                                        >
                                            <X size={14} aria-hidden="true" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div className="flex gap-2">
                            {/* Enter adds the option rather than submitting the whole form. */}
                            <div
                                className="flex-1"
                                onKeyDown={(e) => {
                                    if (e.key !== "Enter") return;
                                    e.preventDefault();
                                    addOption();
                                }}
                            >
                                <label htmlFor="patient-field-new-option" className="sr-only">
                                    {t("center.patientFields.newOption")}
                                </label>
                                <Input
                                    id="patient-field-new-option"
                                    value={newOption}
                                    onChange={(e) => setNewOption(e.target.value)}
                                    placeholder={t("center.patientFields.optionPlaceholder")}
                                    error={submitted && optionsMissing}
                                    aria-invalid={submitted && optionsMissing}
                                />
                            </div>
                            <Button
                                variant="outline"
                                onClick={addOption}
                                startIcon={<Plus size={14} aria-hidden="true" />}
                            >
                                {t("center.patientFields.addOption")}
                            </Button>
                        </div>
                        {submitted && optionsMissing && (
                            <p className="text-xs text-status-critical">{t("center.patientFields.errors.optionsInvalid")}</p>
                        )}
                    </fieldset>
                )}

                <div className="flex flex-col gap-4">
                    <FieldSwitch
                        label={t("center.patientFields.required")}
                        hint={t("center.patientFields.requiredHint")}
                        checked={required}
                        onChange={setRequired}
                    />
                    <FieldSwitch
                        label={t("center.patientFields.unique")}
                        hint={t("center.patientFields.uniqueHint")}
                        checked={unique}
                        onChange={setUnique}
                    />
                </div>

                {error && (
                    <p role="alert" className="text-sm text-status-critical">
                        {t(error)}
                    </p>
                )}

                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose}>
                        {t("common.cancel")}
                    </Button>
                    <Button type="submit" loading={pending} disabled={pending}>
                        {editing ? t("common.save") : t("center.patientFields.create")}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
